import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  Calendar,
  Clock,
  Scissors,
  User,
  Phone,
  Mail,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MapPin,
} from 'lucide-react';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function generateTimeSlots(open, close, durationMinutes = 30) {
  const slots = [];
  const [oh, om] = open.split(':').map(Number);
  const [ch, cm] = close.split(':').map(Number);
  let cur = oh * 60 + om;
  const end = ch * 60 + cm - durationMinutes;
  while (cur <= end) {
    const h = String(Math.floor(cur / 60)).padStart(2, '0');
    const m = String(cur % 60).padStart(2, '0');
    slots.push(`${h}:${m}`);
    cur += 30;
  }
  return slots;
}

function formatDateTR(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

function getNextDays(n) {
  const days = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    days.push({ iso: `${y}-${mo}-${da}`, dayName: DAY_NAMES[d.getDay()], date: d });
  }
  return days;
}

export default function BookingPage() {
  const { businessSlug } = useParams();
  const { t } = useTranslation('public');
  const STEPS = [
    t('booking.steps.service'),
    t('booking.steps.date'),
    t('booking.steps.time'),
    t('booking.steps.info'),
    t('booking.steps.confirm'),
  ];
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');

  const [step, setStep] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [appointmentId, setAppointmentId] = useState('');

  const days = getNextDays(14);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'business_profiles'),
            where('slug', '==', businessSlug),
            where('status', '==', 'active'),
            limit(1)
          )
        );
        if (snap.empty) {
          setProfileError(t('booking.profileNotFound'));
        } else {
          setProfile(snap.docs[0].data());
        }
      } catch {
        setProfileError(t('booking.loadError'));
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, [businessSlug]);

  const availableDays = days.filter((d) => {
    const cfg = profile?.workingHours?.[d.dayName];
    return cfg?.isOpen;
  });

  const timeSlots = selectedDate && profile
    ? (() => {
        const dayName = DAY_NAMES[new Date(selectedDate + 'T12:00').getDay()];
        const cfg = profile.workingHours?.[dayName];
        if (!cfg?.isOpen) return [];
        const duration = selectedService?.durationMinutes || 30;
        return generateTimeSlots(cfg.open, cfg.close, duration);
      })()
    : [];

  const handleSubmit = async () => {
    if (!guestName.trim() || !guestPhone.trim()) {
      setSubmitError(t('booking.infoStep.errors.nameRequired'));
      return;
    }
    if (guestEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())) {
      setSubmitError(t('booking.infoStep.errors.invalidEmail'));
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const fn = httpsCallable(functions, 'createGuestAppointment');
      const payload = {
        businessSlug,
        serviceId: selectedService.id,
        date: selectedDate,
        time: selectedTime,
        guestName: guestName.trim(),
        guestPhone: guestPhone.trim(),
      };
      if (guestEmail.trim()) payload.guestEmail = guestEmail.trim();
      const result = await fn(payload);
      setAppointmentId(result.data.appointmentId);
      setStep(4);
    } catch (err) {
      setSubmitError(err.message || t('booking.infoStep.errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{profileError}</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline text-sm">
            {t('booking.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold text-lg">
              {profile.publicName?.[0] || 'S'}
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-lg leading-tight">{profile.publicName}</h1>
              {profile.description && (
                <p className="text-xs text-gray-500 flex items-center">
                  <MapPin className="h-3 w-3 mr-1" />
                  {profile.description}
                </p>
              )}
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Adım Göstergesi */}
        {step < 4 && (
          <div className="flex items-center justify-between mb-6">
            {STEPS.slice(0, 4).map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      i < step
                        ? 'bg-blue-500 text-white'
                        : i === step
                        ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {i < step ? '✓' : i + 1}
                  </div>
                  <span className={`text-xs mt-1 ${i === step ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                    {label}
                  </span>
                </div>
                {i < 3 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? 'bg-blue-400' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ── Adım 0: Hizmet Seçimi ── */}
        {step === 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-gray-800 mb-4">{t('booking.serviceStep.title')}</h2>
            {(profile.activeServices || []).map((svc) => (
              <button
                key={svc.id}
                onClick={() => { setSelectedService(svc); setStep(1); }}
                className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Scissors className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{svc.name}</p>
                    <p className="text-xs text-gray-500">{t('booking.serviceStep.duration', { minutes: svc.durationMinutes })}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-blue-600 font-semibold">₺{svc.price}</span>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-400 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* ── Adım 1: Tarih Seçimi ── */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4">{t('booking.dateStep.title')}</h2>
            {availableDays.length === 0 && (
              <p className="text-gray-500 text-center py-8">{t('booking.dateStep.noAvailableDays')}</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              {availableDays.map((d) => (
                <button
                  key={d.iso}
                  onClick={() => { setSelectedDate(d.iso); setSelectedTime(''); setStep(2); }}
                  className={`p-4 rounded-xl border text-left transition-all hover:border-blue-400 hover:shadow-sm ${
                    selectedDate === d.iso
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{formatDateTR(d.iso)}</p>
                      <p className="text-xs text-gray-500">{t(`booking.days.${d.dayName}`)}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <button onClick={() => setStep(0)} className="mt-4 flex items-center text-sm text-gray-500 hover:text-gray-700">
              <ChevronLeft className="h-4 w-4 mr-1" /> {t('booking.back')}
            </button>
          </div>
        )}

        {/* ── Adım 2: Saat Seçimi ── */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-1">{t('booking.timeStep.title')}</h2>
            <p className="text-sm text-gray-500 mb-4">{formatDateTR(selectedDate)}</p>
            {timeSlots.length === 0 && (
              <p className="text-gray-500 text-center py-8">{t('booking.timeStep.noSlots')}</p>
            )}
            <div className="grid grid-cols-4 gap-2">
              {timeSlots.map((slot) => (
                <button
                  key={slot}
                  onClick={() => { setSelectedTime(slot); setStep(3); }}
                  className={`py-2.5 rounded-lg border text-sm font-medium transition-all hover:border-blue-400 ${
                    selectedTime === slot
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-blue-50'
                  }`}
                >
                  <Clock className="h-3 w-3 mx-auto mb-0.5" />
                  {slot}
                </button>
              ))}
            </div>
            <button onClick={() => setStep(1)} className="mt-4 flex items-center text-sm text-gray-500 hover:text-gray-700">
              <ChevronLeft className="h-4 w-4 mr-1" /> {t('booking.back')}
            </button>
          </div>
        )}

        {/* ── Adım 3: Bilgiler ── */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4">{t('booking.infoStep.title')}</h2>

            {/* Özet */}
            <div className="bg-blue-50 rounded-xl p-4 mb-5 space-y-1.5 text-sm">
              <div className="flex items-center space-x-2 text-blue-800">
                <Scissors className="h-4 w-4" />
                <span className="font-medium">{selectedService?.name}</span>
                <span className="text-blue-500">• ₺{selectedService?.price}</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-700">
                <Calendar className="h-4 w-4" />
                <span>{formatDateTR(selectedDate)}</span>
                <Clock className="h-4 w-4 ml-1" />
                <span>{selectedTime}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('booking.infoStep.nameLabel')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder={t('booking.infoStep.namePlaceholder')}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('booking.infoStep.phoneLabel')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="tel"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder={t('booking.infoStep.phonePlaceholder')}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('booking.infoStep.emailLabel')}
                  <span className="text-gray-400 text-xs font-normal ml-1">{t('booking.infoStep.emailHint')}</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder={t('booking.infoStep.emailPlaceholder')}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {submitError && (
              <div className="mt-3 flex items-center space-x-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="mt-5 w-full flex items-center justify-center py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-60"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('booking.infoStep.submitting')}</>
              ) : (
                <>{t('booking.infoStep.submit')} <ChevronRight className="h-4 w-4 ml-1" /></>
              )}
            </button>
            <button onClick={() => setStep(2)} className="mt-3 flex items-center text-sm text-gray-500 hover:text-gray-700 mx-auto">
              <ChevronLeft className="h-4 w-4 mr-1" /> {t('booking.back')}
            </button>
          </div>
        )}

        {/* ── Adım 4: Onay ── */}
        {step === 4 && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-9 w-9 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('booking.confirmStep.title')}</h2>
            <p className="text-gray-600 text-sm mb-1">
              <strong>{profile.publicName}</strong> {t('booking.confirmStep.willCall')}
            </p>
            <p className="text-xs text-gray-400 mt-4">{t('booking.confirmStep.appointmentNo', { id: appointmentId?.slice(-6).toUpperCase() })}</p>

            <div className="mt-6 bg-gray-50 rounded-xl p-4 text-left text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('booking.confirmStep.service')}</span>
                <span className="font-medium text-gray-800">{selectedService?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('booking.confirmStep.date')}</span>
                <span className="font-medium text-gray-800">{formatDateTR(selectedDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('booking.confirmStep.time')}</span>
                <span className="font-medium text-gray-800">{selectedTime}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="text-center py-6">
        <p className="text-xs text-gray-400">{t('booking.poweredBy')}</p>
      </div>
    </div>
  );
}
